import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Table,
  Select,
  Button,
  Spin,
  App,
  Space,
  Form,
} from 'antd';
import {
  FileExcelOutlined,
  SearchOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import {
  listCompetitionRegions,
  listAllStates,
  listCitiesByState,
  listClientRoles,
} from '../../services/catalogs';
import { getClientsCountByRole, getHeatmapByState } from '../../services/reports';
import { exportToExcel } from '../../utils/excel';
import HeatmapMexicoMap, { HEATMAP_LEGEND_ITEMS } from '../../components/Reports/HeatmapMexicoMap';
import debounce from 'lodash/debounce';

const HeatmapReport = () => {
  const { message } = App.useApp();

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [heatmapData, setHeatmapData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [form] = Form.useForm();

  const [zones, setZones] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);

  const stateId = Form.useWatch('stateId', form);
  const roleId = Form.useWatch('roleId', form);

  // allRoles = array de perfiles (parentRoleId null), cada uno con subRoles[]
  const profiles = useMemo(() => allRoles || [], [allRoles]);
  const subRoles = useMemo(() => {
    if (!roleId) return [];
    const profile = (allRoles || []).find((r) => Number(r.id) === Number(roleId));
    return profile?.subRoles || [];
  }, [allRoles, roleId]);

  useEffect(() => {
    Promise.all([
      listCompetitionRegions(),
      listAllStates(),
      listClientRoles(),
    ]).then(([z, s, roles]) => {
      setZones(z || []);
      setStates(s || []);
      setAllRoles(roles || []);
    });
  }, []);

  useEffect(() => {
    if (stateId) {
      listCitiesByState(stateId).then(setCities);
    } else {
      setCities([]);
      form.setFieldValue('cityId', undefined);
    }
  }, [stateId, form]);

  const fetchCities = useMemo(
    () =>
      debounce(async (value) => {
        if (!value || !stateId) return;
        setLoadingCities(true);
        try {
          const result = await listCitiesByState(stateId, value);
          setCities(result || []);
        } finally {
          setLoadingCities(false);
        }
      }, 500),
    [stateId]
  );

  const buildParams = useCallback(() => {
    const values = form.getFieldsValue();
    const params = {};
    if (values.zoneId != null) params.zoneId = values.zoneId;
    if (values.stateId != null) params.stateId = values.stateId;
    if (values.cityId != null) params.cityId = values.cityId;
    if (values.roleId != null) params.roleId = values.roleId;
    if (values.subRoleId != null) params.subRoleId = values.subRoleId;
    return params;
  }, [form]);

  const hasAtLeastOneFilter = useCallback(() => {
    return Object.keys(buildParams()).length >= 1;
  }, [buildParams]);

  const loadData = useCallback(async () => {
    if (!hasAtLeastOneFilter()) {
      message.warning('Debe seleccionar al menos un filtro para consultar.');
      return;
    }
    setLoading(true);
    try {
      const params = buildParams();
      const [byRoleRes, heatmapRes] = await Promise.all([
        getClientsCountByRole(params),
        getHeatmapByState(params),
      ]);

      if (byRoleRes?.ok && byRoleRes?.data) {
        setTableData(byRoleRes.data.items || []);
        setTotalCount(byRoleRes.data.total ?? 0);
      } else {
        setTableData([]);
        setTotalCount(0);
      }

      if (heatmapRes?.ok && heatmapRes?.data) {
        setHeatmapData(heatmapRes.data.items || []);
      } else {
        setHeatmapData([]);
      }
    } catch (err) {
      message.error(err?.__normalized?.message || 'Error al obtener el reporte.');
      setTableData([]);
      setHeatmapData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [message, buildParams, hasAtLeastOneFilter]);

  const handleSearch = () => {
    form.validateFields().then(() => loadData());
  };

  const handleClear = () => {
    form.resetFields();
    setTableData([]);
    setHeatmapData([]);
    setTotalCount(0);
    setCities([]);
  };

  const handleExportExcel = () => {
    if (!tableData.length) {
      message.warning('No hay datos para exportar. Ejecute primero la búsqueda.');
      return;
    }
    setExporting(true);
    try {
      const excelData = tableData.map((item) => ({
        Zona: item.zona ?? '',
        Estado: item.estado ?? '',
        Ciudad: item.ciudad ?? '',
        Perfil: item.perfil ?? '',
        Subrol: item.subRol ?? '',
        '#': item.cantidad ?? 0,
      }));
      const columnWidths = [
        { wch: 22 },
        { wch: 20 },
        { wch: 20 },
        { wch: 22 },
        { wch: 22 },
        { wch: 8 },
      ];
      exportToExcel(
        excelData,
        columnWidths,
        'Reportes - Futbol amateur',
        'reporte_futbol_amateur'
      );
      message.success('Archivo Excel exportado correctamente.');
    } catch (err) {
      message.error('Error al exportar a Excel.');
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      title: 'Zona',
      dataIndex: 'zona',
      key: 'zona',
      sorter: (a, b) => (a.zona || '').localeCompare(b.zona || ''),
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      sorter: (a, b) => (a.estado || '').localeCompare(b.estado || ''),
    },
    {
      title: 'Ciudad',
      dataIndex: 'ciudad',
      key: 'ciudad',
      render: (v) => v ?? '-',
      sorter: (a, b) => (a.ciudad || '').localeCompare(b.ciudad || ''),
    },
    {
      title: 'Perfil',
      dataIndex: 'perfil',
      key: 'perfil',
      render: (v) => v ?? '-',
      sorter: (a, b) => (a.perfil || '').localeCompare(b.perfil || ''),
    },
    {
      title: 'Subrol',
      dataIndex: 'subRol',
      key: 'subRol',
      render: (v) => v ?? '-',
      sorter: (a, b) => (a.subRol || '').localeCompare(b.subRol || ''),
    },
    {
      title: '#',
      dataIndex: 'cantidad',
      key: 'cantidad',
      sorter: (a, b) => (a.cantidad ?? 0) - (b.cantidad ?? 0),
      defaultSortOrder: 'descend',
    },
  ];

  const dataSource = tableData.map((item, idx) => ({ ...item, key: idx }));

  return (
    <Card
      title="Reporte - Mapa de calor futbol amateur"
      style={{ margin: 20 }}
      extra={
        <Space>
          <Button
            icon={<SearchOutlined />}
            type="primary"
            onClick={handleSearch}
            loading={loading}
          >
            Buscar
          </Button>
          <Button icon={<ClearOutlined />} onClick={handleClear}>
            Limpiar
          </Button>
          <Button
            icon={<FileExcelOutlined />}
            onClick={handleExportExcel}
            loading={exporting}
            disabled={!tableData.length}
          >
            Exportar Excel
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" onFinish={handleSearch}>
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Form.Item name="zoneId" label="Zona">
              <Select
                allowClear
                placeholder="Zona"
                options={zones.map((z) => ({ value: z.id, label: z.description }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Form.Item name="stateId" label="Estado">
              <Select
                allowClear
                placeholder="Estado"
                options={states.map((s) => ({ value: s.id, label: s.description }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Form.Item name="cityId" label="Ciudad">
              <Select
                allowClear
                showSearch
                filterOption={false}
                onSearch={fetchCities}
                loading={loadingCities}
                placeholder="Ciudad"
                disabled={!stateId}
                options={cities.map((c) => ({ value: c.id, label: c.name }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Form.Item name="roleId" label="Perfil">
              <Select
                allowClear
                placeholder="Perfil"
                options={profiles.map((p) => ({ value: p.id, label: p.name }))}
                onChange={() => form.setFieldValue('subRoleId', undefined)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Form.Item name="subRoleId" label="SubRol">
              <Select
                allowClear
                placeholder="SubRol"
                options={subRoles.map((s) => ({ value: s.id, label: s.name }))}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Row gutter={16} style={{ marginBottom: 24 }} align="stretch">
        <Col xs={24} md={18}>
          <Card size="small">
            <div style={{ height: 380 }}>
              <HeatmapMexicoMap data={heatmapData} />
            </div>
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card size="small" style={{ height: '100%' }}>
            {HEATMAP_LEGEND_ITEMS.map(({ label, color }) => (
              <div
                key={label}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 8,
                  fontSize: 12,
                }}
              >
                <span
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: 2,
                    backgroundColor: color,
                    flexShrink: 0,
                  }}
                />
                <span>{label}</span>
              </div>
            ))}
          </Card>
        </Col>
      </Row>

      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} registros`,
          }}
          scroll={{ x: 'max-content' }}
          summary={() =>
            totalCount > 0 ? (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={5}>
                  <strong>Total general</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <strong>{totalCount}</strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            ) : null
          }
        />
      </Spin>
    </Card>
  );
};

export default HeatmapReport;
